import { ArcElement, Chart as Chartjs, SubTitle, Title, Tooltip } from 'chart.js';

import { useMemo } from 'react';

import Skeleton from 'components/global/Skeleton';

import BaseChart, { CHART_TYPES } from './BaseChart';
import Legend from './CustomChartLegend';
import * as S from './styled/PieChart.styled';
import { useEmptyDataSettings } from './utils';

Chartjs.register(ArcElement, Title, SubTitle, Tooltip);

/**
 * @typedef {object} PieChartProps
 * @property {Array} data - The data to display
 * @property {boolean} loading - The loading state
 * @property {string} width - The width of the chart, default is 200px
 * @property {string} legendPlacement - The placement of the legend, default is bottom (options: bottom, right)
 * @returns {JSX.Element}
 *
 * data example:
 * {
 * 	labels: ['label1', 'label2'],
 * 	datasets: [
 * 		{
 * 			data: [10, 20]
 * 		}
 * 	]
 * }
 */

const PieChartV2 = ({ data, loading, width = '20rem', legendPlacement = 'bottom' }) => {
	const noData = useMemo(() => !data || data.length === 0 || data?.labels?.length === 0, [data]);
	const { emptyData } = useEmptyDataSettings();

	const pieChartOptions = useMemo(
		() => ({
			datasets: {
				pie: {
					hoverOffset: 4
				}
			},
			layout: {
				padding: 2
			},
			plugins: {
				legend: {
					display: false
				},
				tooltip: {
					enabled: true,
					// if there is no data, the tooltip contains only the title without the value
					...((!data || data.length === 0 || data?.labels?.length === 0) && {
						callbacks: {
							label: () => ''
						}
					})
				}
			}
		}),
		[data, noData, legendPlacement]
	);

	if (loading) {
		return (
			<S.Container>
				<Skeleton width={width} height={width} borderRadius="100%" />
			</S.Container>
		);
	}

	return (
		<S.Container legendPlacement={legendPlacement}>
			<S.ChartContainer width={width}>
				<BaseChart type={CHART_TYPES.pie} data={noData ? emptyData : data} options={{ ...pieChartOptions }} />
			</S.ChartContainer>
			{!noData && <Legend minWidth={width} data={data} legendPlacement={legendPlacement} />}
		</S.Container>
	);
};

export default PieChartV2;
